import {
  CircularProgress,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { NumberFormatCustom } from "helper/numberFormat";
import { useTranslation } from "react-i18next";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  rxSpecialCharAccountName,
  rxSpecialCharChannelName,
} from "helper/regex";

const CoreInput = (props) => {
  const {
    required,
    className,
    name,
    label,
    placeholder,
    InputLabelProps,
    inputProps,
    InputProps,
    readOnly,
    type,
    loading,
    defaultValue,
    multiline,
    minRows,
    maxRows,
    hidden,
    helperText,
    allowTranslation,
    disabled,
    rules,
    transform,
    title,
    size = "medium",
    autoFocus = false,
    isChangeData = false,
    ...restProps
  } = props;
  const [showPassword, setShowPassword] = useState(false);

  const { control } = useFormContext();

  const { t } = useTranslation("common");
  const { t: tUAA } = useTranslation("uaa");

  const InputLabelPropsDefault = _.isNil(InputLabelProps)
    ? { shrink: true, required }
    : InputLabelProps;

  if (hidden) {
    return null;
  }
  const transformCustom = fnTransform(transform, type);
  const handleShowPassword = () => setShowPassword((show) => !show);

  // const handleCopy = (event) => {
  //   const inputValue = event.target.value;
  //   if (inputValue.length > maxLength) {
  //     const selectedText = inputValue.substring(
  //       event.target.selectionStart,
  //       event.target.selectionEnd
  //     );
  //     event.clipboardData.setData('text/plain', selectedText.substring(0, maxLength));
  //     event.preventDefault();
  //   }
  // };

  return (
    <>
      <div className={className}>
        <Controller
          control={control}
          name={name}
          defaultValue={defaultValue}
          rules={{
            ...rules,
            required: required ? "message.emptyField" : false,
          }}
          render={({
            field: { onChange, value, ref },
            fieldState: { error },
          }) => {
            return (
              <>
                <TextField
                  fullWidth
                  autoFocus={autoFocus}
                  sx={{
                    ...(isChangeData && {
                      "& .MuiInputBase-root.Mui-disabled": {
                        "& > fieldset": {
                          borderColor: "#F89E19",
                        },
                      },
                    }),
                    "& .MuiInputBase-input": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                  type={
                    ["phone", "number"].includes(type) || showPassword
                      ? "text"
                      : type
                  }
                  label={label}
                  placeholder={
                    placeholder ??
                    t("form.input.placeholder", { label: title ?? label })
                  }
                  onChange={(e) => {
                    onChange(transformCustom.output(e));
                  }}
                  onBlur={(e) => {
                    onChange(e.target.value?.trim());
                  }}
                  value={transformCustom.input(value)}
                  loading={loading}
                  inputRef={ref}
                  multiline={multiline}
                  minRows={minRows}
                  maxRows={maxRows}
                  disabled={disabled}
                  error={!!error}
                  helperText={
                    error &&
                    (error.type === "validate"
                      ? tUAA(error.message, {
                          field: title ?? label,
                          label: title ?? label,
                        })
                      : t(error.message, { field: title ?? label }))
                  }
                  InputLabelProps={{
                    ...InputLabelPropsDefault,
                    style: isChangeData ? { color: "#F89E19" } : null,
                  }}
                  inputProps={{
                    readOnly,
                    ...inputProps,
                  }}
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                  InputProps={{
                    ...InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}

                        {type === "password" ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleShowPassword}
                              // onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ) : null}
                      </>
                    ),
                    ...(type === "number" && {
                      inputComponent: NumberFormatCustom,
                    }),
                  }}
                  {...restProps}
                  size={size}
                />
                {helperText && <FormHelperText>{helperText}</FormHelperText>}
              </>
            );
          }}
        />
      </div>
    </>
  );
};

CoreInput.defaultProps = {
  loading: false,
  className: null,
  label: null,
  placeholder: null,
  transform: {
    input: (value) => value ?? "",
    output: (e) => e,
  },
  InputLabelProps: null,
  inputProps: null,
  InputProps: null,
  defaultValue: "",
  required: false,
  type: "text",
  multiline: false,
  minRows: 5,
  maxRows: null,
  disabled: false,
  allowTranslation: false,
};

CoreInput.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  loading: PropTypes.bool,
  transform: PropTypes.object,
  InputLabelProps: PropTypes.object,
  inputProps: PropTypes.object,
  InputProps: PropTypes.object,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  type: PropTypes.string,
  onKeyPress: PropTypes.func,
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func,
  multiline: PropTypes.bool,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  hidden: PropTypes.bool,
  helperText: PropTypes.any,
  rules: PropTypes.object,
  allowTranslation: PropTypes.bool,
  isChangeData: PropTypes.bool,
  autoFocus: PropTypes.bool,
};

export default React.memo(CoreInput);

const fnTransform = (transform, type) => {
  let rs = transform;
  switch (type) {
    case "number":
      rs = {
        input: (value) => value,
        output: (e) => {
          const output = e.target.value;

          return output === "" ? null : Number(output);
        },
      };
      break;
    case "phone":
      rs = {
        input: (value) => value,
        output: (e) => {
          const output = e.target.value;
          const number = String(output).replace(/\D/g, "");
          return number;
        },
      };
      break;
    case "fullName":
      rs = {
        input: (value) => value,
        output: (e) => {
          return e.target.value.toUpperCase();
        },
      };
      break;
    case "clearSpeacialChar":
      rs = {
        input: (value) => value,
        output: (e) => {
          return e.target.value.replace(rxSpecialCharChannelName, "");
        },
      };
      break;
    case "clearSpeacialCharByAccount":
      rs = {
        input: (value) => value,
        output: (e) => {
          return e.target.value.replace(rxSpecialCharAccountName, "");
        },
      };
      break;
    default:
      break;
  }
  return rs;
};
